import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { walletAPI, userAPI } from '../../../services/api';

// Async thunks
export const fetchWalletBalance = createAsyncThunk(
  'wallet/fetchBalance',
  async (_, { rejectWithValue }) => {
    try {
      const response = await walletAPI.getBalance();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateWalletAfterSpin = createAsyncThunk(
  'wallet/updateAfterSpin',
  async (userBalance, { rejectWithValue }) => {
    try {
      // Map backend UserBalanceDTO fields to Redux state field names
      return {
        datumCoins: userBalance.coins ?? userBalance.datumCoins ?? 0,
        tickets: userBalance.tickets ?? 0,
        ton: userBalance.tonCoins ?? userBalance.ton ?? 0,
        boostTickets: userBalance.boostTickets ?? 0,
        lastUpdate: new Date().toISOString()
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ── Linked TON wallet (single source of truth, persisted on the backend) ──────
// Both the Lucky Spin page and the TON Withdrawal page read/write this so their
// "Connect wallet" buttons stay in sync regardless of where the user links.
export const fetchLinkedWallet = createAsyncThunk(
  'wallet/fetchLinkedWallet',
  async (_, { rejectWithValue }) => {
    try {
      const data = await userAPI.getTonWallet();
      return data?.address || '';
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveLinkedWallet = createAsyncThunk(
  'wallet/saveLinkedWallet',
  async (address, { rejectWithValue }) => {
    try {
      await userAPI.saveTonWallet(address);
      return address;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const unlinkWallet = createAsyncThunk(
  'wallet/unlinkWallet',
  async (_, { rejectWithValue }) => {
    try {
      await userAPI.unlinkTonWallet();
      return '';
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  datumCoins: 0,
  tickets: 0,
  ton: 0,
  boostTickets: 0,
  linkedWallet: '',
  lastUpdate: null,
  isLoading: false,
  error: null,
};

const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    // Apply a PlayerBalanceDTO returned inline by an action (achievement claim, purchase…)
    // so the header updates immediately, without waiting for a separate GET /v1/wallet/balance.
    setBalanceFromServer: (state, action) => {
      const b = action.payload;
      if (!b) return;
      state.datumCoins = b.datumCoins ?? b.coins ?? state.datumCoins;
      state.tickets = b.tickets ?? state.tickets;
      state.ton = b.ton ?? b.tonCoins ?? state.ton;
      state.boostTickets = b.boostTickets ?? state.boostTickets;
      state.lastUpdate = new Date().toISOString();
    },
    updateFromWebSocket: (state, action) => {
      const { datumCoins, tickets, ton } = action.payload;
      state.datumCoins = datumCoins;
      state.tickets = tickets;
      state.ton = ton;
      state.lastUpdate = new Date().toISOString();
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    decrementTicket: (state) => {
      state.tickets = Math.max(0, state.tickets - 1);
    },
    decrementBoostTicket: (state) => {
      state.boostTickets = Math.max(0, state.boostTickets - 1);
    },
    // Откат оптимистического декремента (используется при 429 / ошибке спина в autospin)
    incrementTicket: (state) => {
      state.tickets += 1;
    },
    incrementBoostTicket: (state) => {
      state.boostTickets += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWalletBalance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWalletBalance.fulfilled, (state, action) => {
        state.isLoading = false;
        // Backend returns coins/tonCoins; some paths return datumCoins/ton — handle both
        state.datumCoins = action.payload.datumCoins ?? action.payload.coins ?? state.datumCoins;
        state.tickets = action.payload.tickets ?? state.tickets;
        state.ton = action.payload.ton ?? action.payload.tonCoins ?? state.ton;
        state.boostTickets = action.payload.boostTickets ?? state.boostTickets;
        state.lastUpdate = new Date().toISOString();
      })
      .addCase(fetchWalletBalance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateWalletAfterSpin.fulfilled, (state, action) => {
        // Only update boost tickets immediately — coins/tickets/ton are deferred to fetchWalletBalance
        // so the balance bar doesn't jump before the reward modal appears.
        state.boostTickets = action.payload.boostTickets ?? state.boostTickets;
        state.lastUpdate = action.payload.lastUpdate;
      })
      // ── Linked TON wallet ───────────────────────────────────────────────────
      .addCase(fetchLinkedWallet.fulfilled, (state, action) => {
        state.linkedWallet = action.payload;
      })
      .addCase(saveLinkedWallet.fulfilled, (state, action) => {
        state.linkedWallet = action.payload;
      })
      .addCase(unlinkWallet.fulfilled, (state) => {
        state.linkedWallet = '';
      });
  },
});

export const {
  setBalanceFromServer,
  updateFromWebSocket,
  clearError,
  setLoading,
  decrementTicket,
  decrementBoostTicket,
  incrementTicket,
  incrementBoostTicket,
} = walletSlice.actions;
export default walletSlice.reducer;
